<template>
  <div
    v-show="visible"
    ref="root"
    class="tj-highlight-toolbar"
    :class="[{ wrap }, placement === 'below' ? 'pos-below' : 'pos-above']"
    :style="{ left: x + 'px', top: y + 'px' }"
    @mousedown.prevent
    role="dialog"
    aria-live="polite"
  >
    <!-- Add highlight -->
    <template v-if="mode === 'add'">
      <span class="item"
        ><svg width="25" height="25" viewBox="0 0 25 25">
          <path
            d="M13.7 15.96l5.2-9.38-4.72-2.62-5.2 9.38 4.72 2.62zm-.5.89l-1.3 2.37-1.26.54-.7 1.26-3.8-.86 1.23-2.22-.2-1.35 1.31-2.37 4.73 2.62z"
            fill-rule="evenodd"
          ></path>
        </svg>
        {{ $t('unitStudy.highlights.toolbarLabel') }}</span
      >

      <span
        v-for="c in colors"
        :key="c"
        class="color-dot"
        :style="{ backgroundColor: c }"
        @pointerdown.stop.prevent="$emit('highlight', c)"
        @click.stop.prevent="$emit('highlight', c)"
      />
    </template>
    <!-- Remove + quick recolor (no Modify label) -->
    <template v-else>
      <span class="item" @mousedown.prevent="$emit('remove')">
        <svg width="25" height="25" viewBox="0 0 25 25" aria-hidden="true">
          <path
            d="M13.7 15.96l5.2-9.38-4.72-2.62-5.2 9.38 4.72 2.62zm-.5.89l-1.3 2.37-1.26.54-.7 1.26-3.8-.86 1.23-2.22-.2-1.35 1.31-2.37 4.73 2.62z"
            fill-rule="evenodd"
          />
        </svg>
        {{ $t('unitStudy.highlights.remove') }}
      </span>
      <span class="seperator" aria-hidden="true"></span>
      <span
        v-for="c in colors"
        :key="c"
        class="color-dot"
        :style="{ backgroundColor: c }"
        @pointerdown.stop.prevent="$emit('recolor', c)"
        @click.stop.prevent="$emit('recolor', c)"
        :aria-label="$t('unitStudy.highlights.changeColorAriaLabel')"
      />
    </template>
  </div>
</template>

<script setup>
  import { ref } from 'vue'

  const root = ref(null)

  defineProps({
    visible: { type: Boolean, default: false },
    x: { type: Number, default: 0 },
    y: { type: Number, default: 0 },
    mode: { type: String, default: 'add' }, // 'add' | 'remove'
    wrap: { type: Boolean, default: false }, // NEW
    placement: { type: String, default: 'above' }, // 'above' | 'below'
  })
  defineEmits(['highlight', 'remove', 'recolor'])

  // 👇 expose the element so parent can measure it
  defineExpose({ el: root })

  const colors = [
    '#ffeb3b',
    '#64b5f6',
    '#a5d867',
    '#ffb74d',
    '#e57373',
    '#9c27b0',
  ]
  // yellow, blue, green, orange, red, purple
</script>

<style scoped>
  .tj-highlight-toolbar {
    position: fixed;
    transform: translate(-50%, calc(-100% - 8px)); /* center + 8px above */
    background: #383935;
    color: #fff;
    padding: 8px 10px;
    border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
    z-index: 9999; /* above Vuetify stuff */
    display: inline-flex;
    align-items: center;
    gap: 10px;
    white-space: nowrap;

    /* allow clicks on items but don't intercept page selection */
    pointer-events: none;
    max-width: 92vw;
  }

  .tj-highlight-toolbar.wrap {
    white-space: normal; /* allow wrapping */
    flex-wrap: wrap; /* dots flow to next line */
    row-gap: 8px;
  }

  /* placement modifiers */
  .tj-highlight-toolbar.pos-above {
    transform: translate(-50%, calc(-100% - 8px));
  }
  .tj-highlight-toolbar.pos-below {
    transform: translate(-50%, 8px);
  }
  /* arrows only on desktop (fine pointer) */
  @media (hover: hover) and (pointer: fine) {
    .tj-highlight-toolbar.pos-above::after,
    .tj-highlight-toolbar.pos-below::after {
      content: '';
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
    }
    .tj-highlight-toolbar.pos-above::after {
      bottom: -6px;
      border-top: 6px solid #383935;
    }
    .tj-highlight-toolbar.pos-below::after {
      top: -6px;
      border-bottom: 6px solid #383935;
    }
  }

  /* prevent overflow and allow wrapping when needed */
  .tj-highlight-toolbar {
    max-width: 92vw;
    white-space: nowrap;
  }
  .tj-highlight-toolbar.wrap {
    white-space: normal;
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .tj-highlight-toolbar .item {
    pointer-events: auto;
  }

  .item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }
  .item:hover {
    color: rgb(165, 216, 103);
  }
  .item:hover path {
    fill: rgb(165, 216, 103);
  }
  path {
    fill: #fff;
  }
  .color-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    cursor: pointer;
    pointer-events: auto;
    border: 2px solid #fff;
  }
  .color-dot:hover {
    transform: scale(1.1);
  }
  /* In the toolbar's CSS or a global */
  @media (pointer: coarse) {
    .color-dot {
      width: 24px;
      height: 24px;
      border-width: 3px;
    }
    .tj-highlight-toolbar {
      gap: 12px;
      padding: 10px 12px;
    }
    .color-dot.is-active {
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.6);
    }
  }
</style>
